import React, { useState, useEffect } from 'react';
import {
  HiTrendingUp,
  HiTrendingDown,
  HiReceiptRefund,
  HiShoppingCart,
  HiUserGroup,
  HiInformationCircle
} from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatPKR } from '../utils/currencyFormatter';
import { API } from '../config';
import axios from 'axios';
import './ProfitLoss.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getPeriodLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  const fmt = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  return `${fmt(startDate)} – ${fmt(endDate)}`;
};

const getYears = () => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2];
};

const getLocalDateString = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const todayStr = () => getLocalDateString(new Date());

const SCOPE_ALL = 'all';

const ProfitLoss = () => {
  const { user } = useAuth();
  const { hasBranches, effectiveBranchId, currentBranch, isAdmin, isManager, branches } = useBranch();
  const { error } = useNotification();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user?.business_id || '');
  const [mode, setMode] = useState('month'); // 'month' | 'range'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [fromDate, setFromDate] = useState(todayStr());
  const [toDate, setToDate] = useState(todayStr());
  // Scope: 'all' = whole business (all branches), or branch id for single branch. Only admin/super_admin can use 'all'.
  const [viewScope, setViewScope] = useState(SCOPE_ALL);

  const canViewAllBranches = isAdmin || user?.role === 'super_admin';
  const isWholeBusiness = canViewAllBranches && viewScope === SCOPE_ALL;
  const effectiveScopeBranchId = canViewAllBranches
    ? (viewScope === SCOPE_ALL ? null : (viewScope || effectiveBranchId))
    : effectiveBranchId;

  useEffect(() => {
    if (user?.role === 'super_admin') {
      axios.get(`${API}/businesses/all`)
        .then(res => {
          const list = res.data.businesses || [];
          setBusinesses(list);
          if (!selectedBusinessId && list.length > 0) setSelectedBusinessId(list[0].id);
        })
        .catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    const businessId = user?.role === 'super_admin' ? selectedBusinessId : user?.business_id;
    if (!businessId || !hasBranches) {
      setLoading(false);
      setReport(null);
      return;
    }
    if (!isWholeBusiness && !effectiveScopeBranchId) {
      setLoading(false);
      setReport(null);
      return;
    }
    setLoading(true);
    const base = `${API}/reports/business/${businessId}`;
    const branchParam = isWholeBusiness ? '' : `branchId=${effectiveScopeBranchId}`;
    const url = mode === 'range'
      ? `${base}/profit-loss/range?fromDate=${fromDate}&toDate=${toDate}${branchParam ? `&${branchParam}` : ''}`
      : `${base}/profit-loss?year=${selectedYear}&month=${selectedMonth}${branchParam ? `&${branchParam}` : ''}`;
    axios.get(url)
      .then(res => {
        setReport(mode === 'range' ? { as_whole: res.data } : res.data);
      })
      .catch(err => error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [mode, selectedBusinessId, selectedYear, selectedMonth, fromDate, toDate, effectiveScopeBranchId, isWholeBusiness, hasBranches, user?.role, user?.business_id]);

  const data = report?.as_whole;
  const period = data?.period;
  const periodLabel = period ? getPeriodLabel(period.start_date, period.end_date) : '';
  const branchName = currentBranch?.name || data?.branch_name || 'Branch';
  const netProfit = parseFloat(data?.net_profit) || 0;
  const isProfit = netProfit >= 0;
  const netRevenue = parseFloat(data?.revenue?.net_revenue) || 0;
  const grossProfitMargin = parseFloat(data?.gross_profit_margin) || 0;
  const netProfitMargin = parseFloat(data?.net_profit_margin) || 0;
  const salesCount = parseInt(data?.revenue?.sales_count, 10) || 0;

  return (
    <Layout>
      <div className="profit-loss-page">
        <div className="pl-header">
          <div className="pl-header-top">
            <div>
              <h1 className="pl-title">Profit & Loss</h1>
              <p className="pl-subtitle">Branch-wise revenue, cost of goods sold, and operating expenses. Net profit is shown after removing salaries and shop expense for the period.</p>
            </div>
          </div>

          {!hasBranches && (
            <div className="pl-banner no-branches-banner">
              <strong>You need a branch to view Profit & Loss.</strong> P&L is calculated per branch. Create a branch from <a href="/branches">Branches</a>, then return here.
            </div>
          )}

          {hasBranches && canViewAllBranches && !isWholeBusiness && !effectiveScopeBranchId && (
            <div className="pl-banner no-branches-banner">
              Select a branch below or choose &quot;All branches&quot; to view Profit & Loss for the whole business.
            </div>
          )}

          {hasBranches && (isWholeBusiness || effectiveScopeBranchId || canViewAllBranches) && (
            <div className="pl-filters-bar">
              {canViewAllBranches && (
                <div className="pl-filter">
                  <label>View</label>
                  <select
                    value={viewScope}
                    onChange={(e) => {
                      const v = e.target.value;
                      setViewScope(v === SCOPE_ALL ? SCOPE_ALL : (v ? parseInt(v, 10) : SCOPE_ALL));
                    }}
                  >
                    <option value={SCOPE_ALL}>All branches</option>
                    {(branches || []).map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {user?.role === 'super_admin' && (
                <div className="pl-filter">
                  <label>Business</label>
                  <select value={selectedBusinessId} onChange={(e) => setSelectedBusinessId(e.target.value)}>
                    <option value="">Select</option>
                    {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
              <div className="pl-filter">
                <label>Period</label>
                <select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="month">By month</option>
                  <option value="range">By date range</option>
                </select>
              </div>
              {mode === 'month' && (
                <>
                  <div className="pl-filter">
                    <label>Month</label>
                    <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
                      {MONTHS_SHORT.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div className="pl-filter">
                    <label>Year</label>
                    <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
                      {getYears().map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </>
              )}
              {mode === 'range' && (
                <>
                  <div className="pl-filter">
                    <label>From date</label>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </div>
                  <div className="pl-filter">
                    <label>To date</label>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <Loader message="Loading report…" variant="block" />
        ) : data ? (
          <div className="pl-body">
            <div className="pl-period-bar">
              <span className="pl-period-branch">{branchName}</span>
              <span className="pl-period-range">{periodLabel}</span>
              {salesCount >= 0 && (
                <span className="pl-period-meta">{salesCount} invoice{salesCount !== 1 ? 's' : ''} in period</span>
              )}
            </div>

            <div className="pl-kpis">
              <div className={`pl-kpi pl-kpi-net ${isProfit ? 'profit' : 'loss'}`}>
                <span className="pl-kpi-label">Net {isProfit ? 'Profit' : 'Loss'}</span>
                <span className="pl-kpi-value">
                  {isProfit ? <HiTrendingUp /> : <HiTrendingDown />}
                  {formatPKR(data.net_profit)}
                </span>
                <span className="pl-kpi-meta">{netProfitMargin.toFixed(1)}% of revenue</span>
              </div>
              <div className="pl-kpi">
                <span className="pl-kpi-label">Net Revenue</span>
                <span className="pl-kpi-value">{formatPKR(netRevenue)}</span>
                <span className="pl-kpi-meta">After returns</span>
              </div>
              <div className="pl-kpi">
                <span className="pl-kpi-label">Gross Profit Margin</span>
                <span className="pl-kpi-value">{grossProfitMargin.toFixed(1)}%</span>
                <span className="pl-kpi-meta">{formatPKR(data.gross_profit)} gross profit</span>
              </div>
              <div className="pl-kpi">
                <span className="pl-kpi-label">Net Profit Margin</span>
                <span className="pl-kpi-value">{netProfitMargin.toFixed(1)}%</span>
                <span className="pl-kpi-meta">Of net revenue</span>
              </div>
            </div>

            <div className="pl-statement">
              <section className="pl-section pl-section-revenue">
                <h3 className="pl-section-title">
                  <HiReceiptRefund className="pl-section-icon" />
                  Revenue
                </h3>
                <div className="pl-rows">
                  <div className="pl-row">
                    <span>Gross Sales</span>
                    <span className="pl-amount">{formatPKR(data.revenue?.gross_sales)}</span>
                  </div>
                  <div className="pl-row pl-row-sub">
                    <span>Sale Returns (amount refunded)</span>
                    <span className="pl-amount neg">({formatPKR(data.revenue?.sale_returns)})</span>
                  </div>
                  {(parseFloat(data.revenue?.retained_from_returns) || 0) > 0 && (
                    <div className="pl-row pl-row-sub">
                      <span>Retained from returns (refund less than invoice)</span>
                      <span className="pl-amount">{formatPKR(data.revenue?.retained_from_returns)}</span>
                    </div>
                  )}
                  <div className="pl-row pl-row-total">
                    <span>Net Revenue</span>
                    <span className="pl-amount">{formatPKR(data.revenue?.net_revenue)}</span>
                  </div>
                  {salesCount >= 0 && (
                    <div className="pl-row pl-row-meta">
                      <span>Invoices (confirmed)</span>
                      <span>{salesCount}</span>
                    </div>
                  )}
                </div>
              </section>

              <section className="pl-section pl-section-cogs">
                <h3 className="pl-section-title">
                  <HiShoppingCart className="pl-section-icon" />
                  Cost of Goods Sold
                </h3>
                <div className="pl-rows">
                  <div className="pl-row pl-row-total">
                    <span>COGS (cost of sold items)</span>
                    <span className="pl-amount neg">{formatPKR(data.cost_of_goods_sold)}</span>
                  </div>
                </div>
              </section>

              <section className="pl-section pl-section-gross">
                <h3 className="pl-section-title">Gross Profit</h3>
                <div className="pl-rows">
                  <div className="pl-row pl-row-total pl-row-highlight">
                    <span>Gross Profit</span>
                    <span className={`pl-amount ${parseFloat(data.gross_profit) >= 0 ? 'pos' : 'neg'}`}>
                      {formatPKR(data.gross_profit)} ({grossProfitMargin.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              </section>

              <section className="pl-section pl-section-opex">
                <h3 className="pl-section-title">
                  <HiUserGroup className="pl-section-icon" />
                  Operating Expenses
                </h3>
                <div className="pl-rows">
                  <div className="pl-row pl-row-sub">
                    <span>Tailoring (standalone jobs)</span>
                    <span className="pl-amount neg">{formatPKR(data.operating_expenses?.tailoring_standalone)}</span>
                  </div>
                  <div className="pl-row pl-row-sub">
                    <span>Tailoring (work orders)</span>
                    <span className="pl-amount neg">{formatPKR(data.operating_expenses?.tailoring_work_orders)}</span>
                  </div>
                  <div className="pl-row pl-row-sub">
                    <span>Salary & Advances</span>
                    <span className="pl-amount neg">{formatPKR(data.operating_expenses?.salary_and_advances)}</span>
                  </div>
                  <div className="pl-row pl-row-sub">
                    <span>Shop Expense</span>
                    <span className="pl-amount neg">{formatPKR(data.operating_expenses?.shop_expense)}</span>
                  </div>
                  <div className="pl-row pl-row-total">
                    <span>Total Operating Expenses</span>
                    <span className="pl-amount neg">{formatPKR(data.operating_expenses?.total)}</span>
                  </div>
                  {(data.stock_transfer_variance != null && Math.abs(parseFloat(data.stock_transfer_variance)) > 0.001) && (
                    <div className="pl-row pl-row-sub">
                      <span>Stock transfer variance (transfer price vs cost)</span>
                      <span className={`pl-amount ${parseFloat(data.stock_transfer_variance) >= 0 ? 'pos' : 'neg'}`}>
                        {formatPKR(data.stock_transfer_variance)}
                      </span>
                    </div>
                  )}
                </div>
              </section>

              <section className={`pl-section pl-section-net ${isProfit ? 'profit' : 'loss'}`}>
                <h3 className="pl-section-title">
                  {isProfit ? <HiTrendingUp className="pl-section-icon" /> : <HiTrendingDown className="pl-section-icon" />}
                  Net {isProfit ? 'Profit' : 'Loss'}
                </h3>
                <div className="pl-rows">
                  <div className="pl-row pl-row-total pl-row-net">
                    <span>Net {isProfit ? 'Profit' : 'Loss'}</span>
                    <span className="pl-amount">
                      {formatPKR(data.net_profit)} ({netProfitMargin.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="pl-row pl-row-sub pl-row-meta">
                    <span>After operating expenses; includes transfer margin when stock is transferred at a different price from cost</span>
                  </div>
                </div>
              </section>

              {(data._note || data.summary) && (
                <div className="pl-note">
                  <HiInformationCircle className="pl-note-icon" />
                  <span>{data.summary || data._note}</span>
                </div>
              )}
            </div>
          </div>
        ) : hasBranches && (isWholeBusiness || effectiveScopeBranchId) ? (
          <div className="pl-empty">
            {mode === 'range' ? 'No data for this date range. Try different dates.' : 'No data for this period. Try another month or year.'}
          </div>
        ) : null}
      </div>
    </Layout>
  );
};

export default ProfitLoss;
